import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { api_routes, db_tables, features, routes } from '@app/consts';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { QuickLinksService } from './services/quick-links.service';
import { FeatureService } from '@app/core/services/feature.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { QuickLink, QuickLinkVerbose } from './models/quick-links.model';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { defer, forkJoin, Observable } from 'rxjs';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';

@Component({
  selector: 'app-quick-links',
  templateUrl: './quick-links.component.html',
  styleUrls: ['./quick-links.component.scss']
})
export class QuickLinksComponent extends SecurityProtectedBase implements OnInit {
  public columns: any[] = [
    { field: "title", title: "Title", tableId: "tbl_QuickLinksTitle", },
    { field: "description", title: "Description", type: "longText", tableId: "tbl_QuickLinksDescription" },
    { field: "orderNumber", title: "Order Number", tableId: "tbl_QuickLinksOrderNumber" },
    { field: "externalUrl", title: "External URL", type: "longText", tableId: "tbl_QuickLinksExternalUrl" },
    { field: "systemLink", subField: "name", title: "System Link", tableId: "tbl_QuickLinksSystemLink" },
  ];

  filterCategories: any[] = [
    { field: "title", title: "Title", tableId: "tbl_QuickLinksTitle", type: "String", dataType: 'String' },
    { field: "description", title: "Description", tableId: "tbl_QuickLinksDescription", type: "String", dataType: 'String' },
    { field: "orderNumber", title: "Order Number", tableId: "tbl_QuickLinksOrderNumber", type: "Number", dataType: 'Number' },
    { field: "externalUrl", title: "External URL", tableId: "tbl_QuickLinksExternalUrl", type: "String", dataType: 'String' },
    { field: "systemLink", title: "System Link", tableId: "tbl_QuickLinksSystemLink", type: "String", dataType: 'String' },
  ];

  sortableColumns: any[] = [
    { field: "title", sortValue: "title" },
    { field: "orderNumber", sortValue: "orderNumber" },
    { field: "externalUrl", sortValue: "externalUrl" },
    { field: "systemLink", sortValue: "systemLink.name" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string = 'orderNumber-desc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string = 'quickLinks';
  tableId: string = db_tables.QuickLinks;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  formId: string = 'frm_PzgFPHnltKpVQm';
  changeReasonFormId: string = 'frm_spMp9G9RWyg0mr';

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private quickLinksService: QuickLinksService,
    featureService: FeatureService
  ) {
    super(featureService, features.QUICK_LINKS);
  }

  ngOnInit(): void {
    if (this.hasFeatureAccess) {
      this.getQuickLinks();
    }
  }

  getQuickLinks() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let combinedFilter = '';
    if (this.filterString && this.searchFilterString) {
      combinedFilter = `(${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = this.filterString;
    } else if (this.searchFilterString) {
      combinedFilter = this.searchFilterString;
    } else {
      combinedFilter = '';
    }
    // Use combinedFilter in the service call

    this.getRequest = this.quickLinksService.getQuickLinks(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  getQuickLink(quickLink: QuickLink) {
    if (!quickLink || !quickLink.id) {
      this.editOrCreateQuickLink(null);
      return;
    }

    this.overlayService.show('Loading');

    this.quickLinksService.getQuickLink(quickLink.id).subscribe(
      {
        next: (v) => {
          this.overlayService.hide();
          this.editOrCreateQuickLink(v);
        },
        error: (e) => this.overlayService.hide(),
        complete: () => this.overlayService.hide()
      }
    );
  }

  editOrCreateQuickLink(quickLink: QuickLinkVerbose) {
    let formData = {
      title: quickLink ? quickLink.title : null,
      description: quickLink ? quickLink.description : null,
      icon: quickLink ? quickLink.icon : null,
      orderNumber: quickLink ? quickLink.orderNumber : null,
      externalUrl: quickLink ? quickLink.externalUrl : null,
      systemLinkId: quickLink && quickLink.systemLink ? quickLink.systemLink.id : null,
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      formData: formData,
      targetEmployeeTablePermissions: null
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((formData) => {
      if (quickLink) {
        this.updateQuickLink(quickLink.id, formData);
      } else {
        this.createQuickLink(formData);
      }
      this.dialogRef.close();
      sub.unsubscribe();
    });
  }

  openConfirmDeleteDialog(itemIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteQuickLinks(itemIds);
        }
      }
    );
  }

  createQuickLink(quickLinkSubmit: any) {
    this.overlayService.show();
    this.quickLinksService.createQuickLink(quickLinkSubmit).subscribe(
      {
        next: (v) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.getQuickLinks();
        },
        error: (e) => this.overlayService.hide(),
        complete: () => this.overlayService.hide()
      }
    );
  }

  updateQuickLink(id: string, quickLinkSubmit: any) {
    this.overlayService.show();

    this.quickLinksService.updateQuickLink(id, quickLinkSubmit).subscribe(
      {
        next: (v) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('UpdatedSuccessfully')}`, 'clear', 'success');
          this.getQuickLinks();
        },
        error: (e) => this.overlayService.hide(),
        complete: () => this.overlayService.hide()
      }
    );
  }

  deleteQuickLinks(quickLinkIds: string[]) {
    const observables = quickLinkIds.map(selectedItem => defer(() => this.quickLinksService.deleteQuickLink(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getQuickLinks();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Title like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getQuickLinks();
  }

}
